import { Button } from '@/components/Button'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { observer } from 'mobx-react-lite'
import type { ReactNode, Ref } from 'react'
import {
  CardItemContent,
  CardList,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { Id } from '@/infra/id'
import type { QuickBooksConnectionsDialogViewModel } from '@/features/bookkeeping/components/BookkeepingPlatformConnectionsDialog/QuickBooksConnectionsDialogViewModel'
import type { XeroConnectionsDialogViewModel } from '@/features/bookkeeping/components/BookkeepingPlatformConnectionsDialog/XeroConnectionsDialogViewModel'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import { Skeleton } from '@/components/Skeleton'
import { BookkeepingAddManualQuickBooksConnectionDialogContainer } from '@/features/bookkeeping/components/BookkeepingAddManualConnectionDialog'
import { formatBookkeepingPlatform } from '@/features/bookkeeping/platform-connections/bookkeeping-platform-connection-formatters'

interface QuickBooksConnectionsDialogContainerProps {
  vm: QuickBooksConnectionsDialogViewModel
}

/**
 * The QuickBooksConnectionsDialogContainer
 */
export const QuickBooksConnectionsDialogContainer = observer(
  ({ vm }: QuickBooksConnectionsDialogContainerProps) => {
    return (
      <BookkeepingPlatformConnectionsDialog
        onAddManually={vm.addConnectionManually}
        isOpen={vm.modal.showing}
        isPending={
          vm.providePlatformConnection.pending || vm.connectQuickBooks.pending
        }
        platform={formatBookkeepingPlatform('QuickBooks')}
        onConnectPlatform={vm.connectQuickBooks}
        onClose={vm.close}
        testId="quickbooks-connections-dialog"
        addManuallyButtonTestId="add-manual-connection-button"
      >
        {vm.isEmpty && vm.scrollProvider.isDone ? (
          <PlatformConnectionEmptyState
            description={`Connect with QuickBooks or manually enter information to link accounts.`}
          />
        ) : (
          <PlatformConnectionList>
            {vm.accounts.map((x) => {
              return (
                <PlatformConnectionListItem
                  key={x.connectionId}
                  title={x.displayName}
                  subtitle={`Company ID: ${x.realmId}`}
                  isDisabled={vm.providePlatformConnection.pending}
                  onSelect={() => vm.providePlatformConnection(x)}
                />
              )
            })}
            <PlatformConnectionInfiniteScrollSkeletonList vm={vm} />
          </PlatformConnectionList>
        )}

        <BookkeepingAddManualQuickBooksConnectionDialogContainer
          vm={vm.bookkeepingAddManualConnectionsDialogVm}
        />
      </BookkeepingPlatformConnectionsDialog>
    )
  },
)

interface XeroConnectionsDialogContainerProps {
  vm: XeroConnectionsDialogViewModel
}

/**
 * The XeroConnectionsDialogContainer
 */
export const XeroConnectionsDialogContainer = observer(
  ({ vm }: XeroConnectionsDialogContainerProps) => {
    return (
      <BookkeepingPlatformConnectionsDialog
        isOpen={vm.modal.showing}
        isPending={
          vm.providePlatformConnection.pending || vm.connectXero.pending
        }
        platform={formatBookkeepingPlatform('Xero')}
        onConnectPlatform={vm.connectXero}
        onClose={vm.close}
        testId="xero-connections-dialog"
      >
        {vm.isEmpty && vm.scrollProvider.isDone ? (
          <PlatformConnectionEmptyState
            description={`Connect with Xero to link accounts.`}
          />
        ) : (
          <PlatformConnectionList>
            {vm.accounts.map((x) => {
              return (
                <PlatformConnectionListItem
                  key={x.connectionId}
                  title={x.displayName}
                  subtitle={Id.assert(x.tenantId)}
                  isDisabled={vm.providePlatformConnection.pending}
                  onSelect={() => vm.providePlatformConnection(x)}
                />
              )
            })}
            <PlatformConnectionInfiniteScrollSkeletonList vm={vm} />
          </PlatformConnectionList>
        )}
      </BookkeepingPlatformConnectionsDialog>
    )
  },
)

interface BookkeepingPlatformSelectionDialogProps {
  children?: ReactNode
  platform: string
  isOpen: boolean
  isPending?: boolean
  isDisabled?: boolean
  onClose?: () => unknown
  onAddManually?: () => unknown
  onConnectPlatform?: () => unknown
  testId?: string
  addManuallyButtonTestId?: string
}

/**
 * The BookkeepingPlatformConnectionsDialog
 */
export function BookkeepingPlatformConnectionsDialog({
  children,
  platform,
  isOpen,
  isPending,
  onAddManually,
  onClose,
  onConnectPlatform,
  testId,
  addManuallyButtonTestId,
}: BookkeepingPlatformSelectionDialogProps) {
  return (
    <ModalDialog
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>Connect your QuickBooks account</ModalDialogTitle>
        <ModalDialogDescription>
          Select the account that you want to proceed with, or link to a new{' '}
          {platform} account.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>{children}</ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label={`Connect with ${platform}`}
            isDisabled={isPending}
            onPress={onConnectPlatform}
          />

          {onAddManually && (
            <Button
              label="Manually enter information"
              variant="outline"
              isDisabled={isPending}
              onPress={onAddManually}
              testId={addManuallyButtonTestId}
            />
          )}
          <Button
            label="Cancel"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface PlatformConnectionListProps {
  children?: ReactNode
}

/**
 * The PlatformConnectionList
 */
export function PlatformConnectionList({
  children,
}: PlatformConnectionListProps) {
  return <CardList aria-label="Platforms">{children}</CardList>
}

interface PlatformConnectionListItemProps {
  title: string
  subtitle: string
  isDisabled: boolean
  onSelect?: () => void
}

/**
 * The PlatformConnectionListItem
 */
export function PlatformConnectionListItem({
  title,
  subtitle,
  isDisabled,
  onSelect,
}: PlatformConnectionListItemProps) {
  return (
    <CardListItem onAction={onSelect} textValue={title} isDisabled={isDisabled}>
      <CardItemContent isInteractive>
        <CardTitle>{title}</CardTitle>
        <CardSubtitle>{subtitle}</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface PlatformConnectionEmptyStateProps {
  description: string
}

/**
 * The PlatformConnectionEmptyState
 */
function PlatformConnectionEmptyState({
  description,
}: PlatformConnectionEmptyStateProps) {
  return (
    <EmptyState>
      <EmptyStateTitle>There are no accounts linked</EmptyStateTitle>
      <EmptyStateDescription>{description}</EmptyStateDescription>
    </EmptyState>
  )
}

interface PlatformConnectionInfiniteScrollSkeletonListProps {
  vm: QuickBooksConnectionsDialogViewModel | XeroConnectionsDialogViewModel
}

/**
 * The SkeletonListItem
 */
function PlatformConnectionInfiniteScrollSkeletonList({
  vm,
}: PlatformConnectionInfiniteScrollSkeletonListProps) {
  return (
    <SkeletonListInfiniteScrollTrigger
      scroller={vm.scrollProvider}
      maxSkeletons={4}
    >
      {({ varyPercent, ref }) => (
        <CardListItem ref={ref as Ref<HTMLDivElement>} textValue="Platform">
          <CardItemContent isInteractive>
            <CardTitle>
              <Skeleton width={varyPercent(60)} />
            </CardTitle>
            <CardSubtitle>
              <Skeleton width={varyPercent(80)} />
            </CardSubtitle>
          </CardItemContent>
        </CardListItem>
      )}
    </SkeletonListInfiniteScrollTrigger>
  )
}
